import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from 'rxjs/operators';
import { RestService } from './rest.service';

@Injectable({
  providedIn: "root",
})
export class AgenticAiService {
  private restService = inject(RestService);
  private message = new BehaviorSubject<string | null>(null);
  private chatHistory = new BehaviorSubject<any | null>(null);
  private aiApproach = new BehaviorSubject<string | null>(null);
  private isAgenticApproach = new BehaviorSubject<boolean | null>(null);

  /** Public observable for components to subscribe to AI approach changes. */
  public aiApproach$: Observable<string | null> = this.aiApproach.asObservable();
  public isAgenticApproach$: Observable<boolean | null> = this.isAgenticApproach.asObservable();

  setMessage(message: string): void {
    this.message.next(message);
  }

  getMessage() {
    return this.message.getValue();
  }

  setHistory(chatHistory: any): void {
    this.chatHistory.next(chatHistory);
  }

  getHistory() {
    return this.chatHistory.getValue();
  }

  setAiApproach(approach: string | null) {
    this.aiApproach.next(approach);
  }

  getAiApproach(){
    return this.aiApproach.getValue();
  }

  setIsAgenticApproach(value: boolean | null) {
    this.isAgenticApproach.next(value);
  }

  getIsAgenticApproach() {
    return this.isAgenticApproach.getValue();
  }

  processChatHistory(history: any) {
    history?.messages.map((chat: any) => {
      if(chat?.role === 'human') {
        chat.isSender = true;
      }
      return chat;
    });

    return history;
  }

  /**
   * Load applications from backend
   */
  loadApplications(projectId: string, automationId: string): Observable<any[]> {
    return this.restService.getAutomationApplications(projectId, automationId).pipe(
      map((response: any) => {
        if (response?.data) {
          return response.data.map((app: any) => ({
            value: app.id,
            label: app.name,
            ...app
          }));
        }
        return [];
      })
    );
  }

  /**
   * Load pages from backend
   */
  loadPages(projectId: string, automationId: string, applicationId: string): Observable<any[]> {
    return this.restService.getAutomationPage(projectId, automationId, applicationId).pipe(
      map((response: any) => {
        if (response?.data) {
          return response.data.map((page: any) => ({
            value: page.id,
            label: page.name,
            applicationId: page.applicationId,
            ...page
          }));
        }
        return [];
      })
    );
  }

  /**
   * Load UI Objects from backend
   */
  loadUiObjects(projectId: string, automationId: string, automationApplicationId: string, pageId: string): Observable<any[]> {
    return this.restService.getAutomationUiObject(projectId, automationId, automationApplicationId, pageId).pipe(
      map((response: any) => {
        if (response?.data) {
          return response.data.map((uiObj: any) => ({
            value: uiObj.id,
            label: uiObj.name,
            ...uiObj
          }));
        }
        return [];
      })
    );
  }

  /**
   * Load devices from backend
   */
  loadDevices(projectId: string): Observable<any[]> {
    // Replace with actual API call when available
    return this.restService.getDevices(projectId).pipe(
      map((response: any) => {
        if (response?.data) {
          return response.data.map((device: any) => ({
            value: device.id,
            label: device.name || device.deviceName,
            ...device
          }));
        }
        return [];
      })
    );
  }

  /**
   * Get story IDs from test cases by version ID
   */
  getStoryIds(projectId: string, versionId: string): Observable<any> {
    return this.restService.getTestCaseByVersionId(projectId, versionId);
  }

  /**
   * Download Excel for TDG
   */
  downloadExcelTDG(data: any): Observable<Blob> {
    return this.restService.downloadExcelTDG(data);
  }

  /**
   * Download Excel for TCG
   */
  downloadExcelTCG(projectId: string, data: any): Observable<Blob> {
    return this.restService.downloadExcel(projectId, data);
  }

  /**
   * Load LLM connections from backend
   */
  loadLLMConnections(projectId: string): Observable<any[]> {
    return this.restService.getConnections(projectId).pipe(
      map((response: any) => {
        if (response?.data?.LLM) {
          return response.data.LLM.map((llm: any) => ({
            value: llm.id,
            label: llm.connectionName || llm.name,
            ...llm
          }));
        }
        return [];
      })
    );
  }
}
